import type { GivingDetail, GivingInterval } from 'models';
import { useCallback, useMemo } from 'react';
import { message } from 'ui';
import { useUser } from 'features/auth';
import type { TransactionData } from 'features/transactions';
import type { BillingInformationValues, GivingCreateFormValues } from '../components';

type GetPriceIdProperties = {
  amount: number;
  giving: GivingDetail;
  recurringValue?: GivingInterval;
};

type GiveNow = {
  billingInformation?: BillingInformationValues;
  googleRecaptchaKey: string;
  items: Array<{
    amount: number;
    displayName: boolean;
    id: string;
    priceId: string;
    recurring?: GivingInterval;
  }>;
};

type P = {
  formValues: GivingCreateFormValues;
  giving: GivingDetail;
  googleRecaptchaKey: string;
};

type PayerInformation = {
  formValues?: BillingInformationValues;
};

export const useGivingCreate = () => {
  const user = useUser({ requiredAuth: false });
  const viewer = user?.railsUserData;

  const viewerInfoWithCompleteAddress = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      viewer?.city && viewer?.country && viewer?.street && viewer?.zip
        ? {
            ...viewer,
            city: viewer.city,
            country: viewer.country,
            street: viewer.street,
            zip: viewer.zip,
          }
        : undefined,
    [viewer],
  );

  const payerInformation = useCallback(
    ({ formValues }: PayerInformation = {}): { profileUpdate: boolean } & TransactionData['payerInformation'] => {
      if (formValues && !formValues.profileAddress) {
        return {
          apt: formValues.apartment,
          city: formValues.city,
          country: formValues.country,
          email: viewer ? viewer.email : formValues.email,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          profileUpdate: Boolean(formValues.profileUpdate),
          state: formValues.state,
          street: formValues.street,
          zip: formValues.zip,
        };
      }

      if (viewerInfoWithCompleteAddress) {
        const { apt, city, country, email, firstName, lastName, state, street, zip } = viewerInfoWithCompleteAddress;
        return {
          apt,
          city,
          country,
          email,
          firstName,
          lastName,
          profileUpdate: false,
          state,
          street,
          zip,
        };
      }

      const errorMessage = 'User has incomplete address';
      message.error(errorMessage);
      throw new Error(errorMessage);
    },
    [viewer, viewerInfoWithCompleteAddress],
  );

  const getPriceId = useCallback(({ amount, giving, recurringValue }: GetPriceIdProperties): string => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    const stripePriceId = giving.prices?.[recurringValue ?? 'one_time']?.find(
      (item) => item.amount === (recurringValue ? amount : null),
    )?.stripe_id;

    if (!stripePriceId) {
      const errorMessage =
        'We apologize for the inconvenience, but the Id of the selected price option was not found in our system due to a technical error.';
      message.error(errorMessage);
      throw new Error(errorMessage);
    }

    return stripePriceId;
  }, []);

  const createRequestData = useCallback(
    ({ formValues, giving, googleRecaptchaKey }: P): TransactionData => {
      const amount = formValues.showCustomAmount ? formValues.customAmount : formValues.amount;
      const payer = payerInformation({ formValues });

      return {
        items: [
          {
            amount,
            anonymous: !formValues.displayName,
            priceId: getPriceId({ amount, giving, recurringValue: formValues.recurring }),
            showTextContent: Boolean(formValues.reason),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            subjectId: giving?.id,
            subjectType: 'Giving',
            textContent: formValues.reason,
          },
        ],
        payerInformation: payer,
        recaptchaKey: googleRecaptchaKey,
        updateProfile: payer.profileUpdate,
      };
    },
    [getPriceId, payerInformation],
  );

  const createGiveNowRequestData = useCallback(
    ({ billingInformation, googleRecaptchaKey, items }: GiveNow): TransactionData => {
      const payer = payerInformation({ formValues: billingInformation });

      return {
        items: items.map(({ amount, displayName, id, priceId }) => ({
          amount,
          anonymous: !displayName,
          priceId,
          subjectId: id,
          subjectType: 'Giving',
        })),
        payerInformation: payer,
        recaptchaKey: googleRecaptchaKey,
        updateProfile: payer.profileUpdate,
      };
    },
    [payerInformation],
  );

  return useMemo(
    () => ({
      createGiveNowRequestData,
      createRequestData,
      getPriceId,
      payerInformation,
      viewerInfoWithCompleteAddress,
    }),
    [createRequestData, payerInformation, viewerInfoWithCompleteAddress, getPriceId, createGiveNowRequestData],
  );
};
